import { useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, BadgePercent, Check, Loader2 } from "lucide-react";
import { useVehicle } from "@/hooks/useSite";
import { EASE, LineDraw, MaskedLines, Reveal, Stagger, Item } from "@/components/motion/Reveal";
import { SectionHead } from "@/components/site/PageHeader";
import { ModelCard } from "@/components/site/ModelCard";
import { LeadForm } from "@/components/site/LeadForm";
import { formatPKR, fromPrice, slugify, waLink } from "@/lib/format";
import { cn } from "@/lib/utils";
import NotFound from "@/pages/NotFound";

export default function VehicleDetail() {
  const { slug } = useParams();
  const { settings, vehicles, offers = [] } = useOutletContext();
  const { data: v, isLoading, isError } = useVehicle(slug);
  const [color, setColor] = useState(0);
  useEffect(() => setColor(0), [slug]);

  if (isLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div>;
  if (isError || !v) return <NotFound />;

  const short = v.name.replace("JETOUR ", "");
  const image = v.colors?.[color]?.image || v.hero_image;
  const price = fromPrice(v);
  const booking = v.variants?.find((x) => x.booking_amount)?.booking_amount;
  const others = vehicles.filter((x) => x.slug !== slug).slice(0, 3);
  const offer = offers.find((o) => o.vehicle_slug === slug);

  return (
    <article data-testid="vehicle-detail-page">
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-24">
        <div className="grid-texture absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
        <p className="outline-text pointer-events-none absolute right-[-4vw] top-[6%] select-none font-display text-[20vw] font-black uppercase leading-none tracking-[-0.06em]">{short}</p>
        <div className="wrap relative grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal y={12}><Link to="/models" data-testid="vehicle-back-link" className="eyebrow inline-flex items-center gap-2 transition-colors hover:text-white"><ArrowLeft className="h-3 w-3" /> All models</Link></Reveal>
            <Reveal y={12} delay={0.1}>
              <p className="eyebrow mt-8 flex flex-wrap items-center gap-4"><LineDraw className="w-10" />{v.category}{v.badge && <span className="rounded-full border border-gold/40 px-3 py-1 text-gold">{v.badge}</span>}</p>
            </Reveal>
            <MaskedLines as="h1" lines={[short]} className="display-xl mt-6" delay={0.2} />
            <Reveal delay={0.4}><p className="mt-4 font-editorial text-2xl italic text-gold" data-testid="vehicle-tagline">{v.tagline}</p></Reveal>
            <Reveal delay={0.5}><p className="mt-6 max-w-md leading-relaxed text-slate-400">{v.description}</p></Reveal>
            <Reveal delay={0.6} className="mt-8 flex flex-wrap items-end gap-10">
              <div>
                <p className="eyebrow">{price ? "From" : "Pricing"}</p>
                <p className="mt-1 font-display text-3xl font-bold tracking-tight" data-testid="vehicle-price">{price ? formatPKR(price) : "To be announced"}</p>
              </div>
              {booking && (
                <div>
                  <p className="eyebrow">Booking amount</p>
                  <p className="mt-1 font-display text-xl font-semibold text-slate-200" data-testid="vehicle-booking">{formatPKR(booking)}</p>
                </div>
              )}
            </Reveal>
            {offer && (
              <Reveal delay={0.65} className="mt-8">
                <Link to="/offers" data-testid="vehicle-offer-banner" className="group flex items-center gap-4 border border-gold/40 bg-gold/5 px-5 py-4 transition-colors hover:bg-gold/10">
                  <BadgePercent className="h-5 w-5 shrink-0 text-gold" />
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">{offer.badge || "Offer"}{offer.valid_until ? ` · until ${offer.valid_until}` : ""}</p>
                    <p className="truncate font-display font-semibold text-white">{offer.title}{offer.discount_label ? ` — ${offer.discount_label}` : ""}</p>
                  </div>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-gold transition-transform group-hover:rotate-45" />
                </Link>
              </Reveal>
            )}
            <Reveal delay={0.7} className="mt-10 flex flex-wrap gap-4">
              <a href="#book" data-testid="vehicle-test-drive-button" className="btn-crimson">Book a Test Drive <ArrowUpRight className="h-4 w-4" /></a>
              {settings.whatsapp && <a href={waLink(settings.whatsapp, `Hello, I'm interested in the ${v.name}. Please share details and availability.`)} target="_blank" rel="noreferrer" data-testid="vehicle-whatsapp-button" className="btn-ghost">WhatsApp Enquiry</a>}
            </Reveal>
          </div>

          <div className="relative lg:col-span-7">
            <div className="spotlight absolute inset-[-20%]" />
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.img key={image} src={image} alt={v.name} data-testid="vehicle-hero-image" initial={{ opacity: 0, x: 80, filter: "blur(10px)" }} animate={{ opacity: 1, x: 0, filter: "blur(0px)" }} exit={{ opacity: 0, x: -80, filter: "blur(8px)" }} transition={{ duration: 0.9, ease: EASE }} className="relative z-10 w-full object-contain drop-shadow-[0_40px_60px_rgba(0,0,0,0.75)]" />
              </AnimatePresence>
              <div className="absolute -bottom-4 left-1/2 h-12 w-4/5 -translate-x-1/2 rounded-[100%] bg-black/80 blur-2xl" />
            </div>
            {v.colors?.length > 0 && (
              <div className="mt-12 flex flex-wrap items-center justify-center gap-3" data-testid="vehicle-color-switcher">
                {v.colors.map((c, i) => (
                  <button key={c.name} onClick={() => setColor(i)} data-testid={`vehicle-color-${slugify(c.name)}`} className={cn("flex items-center gap-3 rounded-full border px-3 py-2 transition-colors duration-300", i === color ? "border-white/70 bg-white/5" : "border-white/10 hover:border-white/40")}>
                    <span className="h-5 w-5 rounded-full border border-white/20" style={{ background: c.hex }} />
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-200">{c.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {v.quick_specs?.length > 0 && (
        <section className="border-y border-white/10 bg-surface/40" data-testid="vehicle-quick-specs">
          <Stagger className="wrap grid grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-white/10" gap={0.08}>
            {v.quick_specs.map((s) => (
              <Item key={s.label} className="py-10 lg:px-10">
                <p className="eyebrow">{s.label}</p>
                <p className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{s.value}</p>
              </Item>
            ))}
          </Stagger>
        </section>
      )}

      <section className="py-24 sm:py-32">
        <div className="wrap">
          <SectionHead number="01" eyebrow="Variants & Pricing" lines={["Pick your", "configuration."]} />
          <Reveal>
            <div className="overflow-x-auto border border-white/10">
              <table className="w-full min-w-[640px] text-left" data-testid="vehicle-variants-table">
                <thead className="bg-surface">
                  <tr className="eyebrow">
                    <th className="px-6 py-4 font-normal">Variant</th>
                    <th className="px-6 py-4 font-normal">Price</th>
                    <th className="px-6 py-4 font-normal">Booking</th>
                    <th className="px-6 py-4 font-normal">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {v.variants.map((x) => (
                    <tr key={x.name} className="transition-colors hover:bg-white/[0.03]" data-testid={`variant-row-${slugify(x.name)}`}>
                      <td className="px-6 py-5 font-display text-lg font-semibold">{x.name}</td>
                      <td className="px-6 py-5">
                        <p className="font-display text-lg font-semibold">{x.price ? formatPKR(x.price) : "TBA"}</p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{x.price_label}</p>
                      </td>
                      <td className="px-6 py-5 text-slate-300">{x.booking_amount ? formatPKR(x.booking_amount) : "—"}</td>
                      <td className="px-6 py-5 text-sm text-slate-400">{x.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">Prices are ex-factory and subject to change without notice. Contact the showroom for on-road pricing and delivery timelines.</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-24 sm:py-32">
        <div className="wrap grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead number="02" eyebrow="Highlights" lines={["Why the", `${short}.`]} className="mb-10" />
            <Stagger className="space-y-6" gap={0.1}>
              {v.highlights.map((h, i) => (
                <Item key={h} className="flex items-start gap-5 border-l border-white/10 pl-5">
                  <span className="font-mono text-xs text-crimson">0{i + 1}</span>
                  <p className="font-display text-xl font-medium">{h}</p>
                </Item>
              ))}
            </Stagger>
          </div>
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow mb-6">Specifications</p></Reveal>
            <Stagger className="divide-y divide-white/10 border-y border-white/10" gap={0.05}>
              {v.specs.map((s) => (
                <Item key={s.label} className="grid gap-2 py-5 sm:grid-cols-[220px_1fr]">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{s.label}</dt>
                  <dd className="font-display text-base text-white">{s.value}</dd>
                </Item>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {v.features?.length > 0 && (
        <section className="border-t border-white/10 py-24 sm:py-32">
          <div className="wrap">
            <SectionHead number="03" eyebrow="Equipment" lines={["Standard", "equipment."]} />
            <Stagger className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3" gap={0.05}>
              {v.features.map((f) => (
                <Item key={f} className="flex items-start gap-4 bg-obsidian p-6">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-crimson" />
                  <span className="text-sm text-slate-200">{f}</span>
                </Item>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      <section id="book" className="border-t border-white/10 py-24 sm:py-32">
        <div className="wrap grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead number="04" eyebrow="Test Drive" lines={["Drive it", "this week."]} sub={`Request a ${short} test drive at West Canal Road. We'll call to confirm the slot.`} className="mb-0" />
          </div>
          <div className="lg:col-span-7">
            <LeadForm type="test_drive" vehicles={vehicles} defaultVehicle={v.slug} settings={settings} />
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="border-t border-white/10 py-24 sm:py-32">
          <div className="wrap">
            <SectionHead number="05" eyebrow="Also in the showroom" lines={["Other", "JETOURs."]} />
            <div className="grid gap-4 md:grid-cols-3">
              {others.map((o, i) => <ModelCard key={o.slug} vehicle={o} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
